import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import apiClient from '../../../lib/apiClient';

/**
 * CandidateDocumentVault
 * Category-based document storage UI for a single candidate.
 * Replaces the old flat "Documents" tab content in ApplicantDetailModal.
 *
 * Usage: <CandidateDocumentVault applicant={applicant} />
 */

const CATEGORY_ICONS = {
  offer_letter: 'FileCheck',
  resume: 'FileText',
  photo: 'User',
  aadhar: 'CreditCard',
  pan: 'CreditCard',
  bank_passbook: 'Landmark',
  marksheet_10: 'GraduationCap',
  marksheet_12: 'GraduationCap',
  other: 'Folder',
};

const getFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';
  const backendRoot = API_BASE_URL.replace(/\/api\/?$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${backendRoot}${cleanPath}`;
};

const CandidateDocumentVault = ({ applicant, onDocumentUploaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const [pendingUploadCategory, setPendingUploadCategory] = useState(null);

  const load = async () => {
    if (!applicant?.id) return;
    try {
      setLoading(true);
      const res = await apiClient.get(`/job-applications/${applicant.id}/documents-v2`);
      if (res.success) {
        setData(res.data);
        setError(null);
      } else {
        setError(res.error || 'Failed to load documents');
      }
    } catch (err) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicant?.id]);

  const handleFileSelected = async (e, category) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    try {
      setUploadingCategory(category);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);

      const result = await apiClient.post(`/job-applications/${applicant.id}/documents-v2`, formData);

      if (result.success) {
        await load();
        if (typeof onDocumentUploaded === 'function') {
          onDocumentUploaded();
        }
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadingCategory(null);
      setPendingUploadCategory(null);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await apiClient.delete(`/job-applications/${applicant.id}/documents-v2/${documentId}`);
      if (res.success) {
        await load();
      } else {
        setError(res.error || 'Delete failed');
      }
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const triggerUpload = (category) => {
    setPendingUploadCategory(category);
    // slight delay so the hidden input for this category exists before click
    setTimeout(() => {
      document.getElementById(`doc-upload-${category}`)?.click();
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground text-sm">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-error/10 border border-error/20 text-error p-4 rounded-lg text-sm">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { categories = {}, documents = {}, uploaded_count = 0, total_slots = 0 } = data;
  const percent = total_slots > 0 ? Math.round((uploaded_count / total_slots) * 100) : 0;
  const singleCategories = categories ? Object.keys(categories).filter((c) => !categories[c]?.multi) : [];
  const otherFiles = (documents && Array.isArray(documents.other)) ? documents.other : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Documents</h3>
          <p className="text-sm text-muted-foreground">
            {applicant?.name || 'Applicant'} · CAND-{applicant?.id || ''}
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="Download" iconPosition="left" disabled>
          Download All
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 text-error p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">
            {uploaded_count} of {total_slots} documents uploaded
          </span>
          <span className="font-medium text-foreground">{percent}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Single-file category cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {singleCategories.map((cat) => {
          const meta = categories[cat] || {};
          const doc = (documents && Array.isArray(documents[cat])) ? documents[cat][0] : null;
          const isUploading = uploadingCategory === cat;
          const acceptedTypes = Array.isArray(meta.types) ? meta.types.map((t) => `.${t}`).join(',') : '.pdf,.jpg,.jpeg,.png';

          return (
            <div key={cat} className="border border-border rounded-lg p-4">
              <input
                type="file"
                id={`doc-upload-${cat}`}
                className="hidden"
                accept={acceptedTypes}
                onChange={(e) => handleFileSelected(e, cat)}
              />
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  name={CATEGORY_ICONS[cat] || 'FileText'}
                  size={18}
                  className={doc ? 'text-green-600' : 'text-muted-foreground'}
                />
                <p className="font-medium text-foreground text-sm">{meta.label || cat}</p>
              </div>
              {doc ? (
                <p className="text-xs text-green-700 mb-3">
                  Uploaded · {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'Uploaded'}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mb-3">Not uploaded</p>
              )}
              <div className="flex gap-2">
                {doc ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(getFileUrl(doc.storage_key), '_blank')}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => triggerUpload(cat)}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Uploading...' : 'Replace'}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => triggerUpload(cat)}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {/* Other Documents — multi-file card */}
        <div className="border border-border rounded-lg p-4 sm:col-span-2 lg:col-span-1">
          <input
            type="file"
            id="doc-upload-other"
            className="hidden"
            accept={Array.isArray(categories?.other?.types) ? categories.other.types.map((t) => `.${t}`).join(',') : '.pdf,.jpg,.jpeg,.png,.doc,.docx'}
            onChange={(e) => handleFileSelected(e, 'other')}
          />
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Folder" size={18} className={otherFiles.length ? 'text-green-600' : 'text-muted-foreground'} />
            <p className="font-medium text-foreground text-sm">Other Documents</p>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {otherFiles.length} {otherFiles.length === 1 ? 'file' : 'files'}
          </p>

          {otherFiles.length > 0 && (
            <div className="space-y-2 mb-3">
              {otherFiles.map((f) => (
                <div key={f.id} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1.5">
                  <span className="truncate flex-1 mr-2">{f.file_name}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      className="text-primary hover:underline"
                      onClick={() => window.open(getFileUrl(f.storage_key), '_blank')}
                    >
                      View
                    </button>
                    <button
                      className="text-red-600 hover:underline"
                      onClick={() => handleDelete(f.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            iconPosition="left"
            onClick={() => triggerUpload('other')}
            disabled={uploadingCategory === 'other'}
          >
            {uploadingCategory === 'other' ? 'Uploading...' : 'Add File'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CandidateDocumentVault;